import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './CollectionCreativesPage.css';

const SCENE_FAMILY_OPTIONS = [
  { value: 'auto', label: 'Auto (current random behavior)' },
  { value: 'apparel_editorial_single_model', label: 'Apparel Editorial Single Model' },
  { value: 'apparel_editorial_multi_model', label: 'Apparel Editorial Multi Model' },
  { value: 'all_apparel_multi_model', label: 'All Apparel Multi Model' },
  { value: 'all_apparel_styled_group', label: 'All Apparel Styled Group' },
  { value: 'cutpiece_studio_editorial', label: 'Cutpiece Studio Editorial' },
  { value: 'fabric_and_garment_mix', label: 'Fabric And Garment Mix' },
  { value: 'draped_fabric_lifestyle_scene', label: 'Draped Fabric Lifestyle Scene' },
];

const FALLBACK_SETTINGS = {
  placements: {
    home_hero: {
      desktop: { width: 2400, height: 1350 },
      mobile: { width: 1200, height: 1600 },
    },
    collection_banner: {
      desktop: { width: 2400, height: 900 },
      mobile: { width: 1200, height: 1500 },
    },
  },
  output: {
    format: 'jpg',
    jpegQuality: 88,
    qualityMode: 'high',
  },
  promptTemplates: {
    universal_base: [
      'Create one premium ecommerce campaign image for a fabric collection.',
      'Create one single continuous photographic scene only. Do not create a collage, split frame, diptych, triptych, or multiple separate images within one frame.',
      'Use only the provided fabric swatches as the textile source. Every visible patterned textile in the final image must come only from those references.',
      'Use all provided fabric swatches in the final scene. Some fabric swatches may be represented by more than one reference image. If multiple images belong to the same fabric design, treat them as one single fabric identity and use that fabric only once in the final composition.',
      'Preserve the original identity of each selected fabric. Keep motif character, motif scale, repeat density, color family, and overall print feel consistent with the references. Do not invent a new print. Do not merge multiple prints into one new textile design. Do not substitute unrelated fabric patterns.',
      'Each selected fabric may appear only once in the final composition. Do not repeat the same fabric across garments, props, scarves, drapes, backdrops, or accessories. Every fabric must remain visually separate and distinct.',
      'Style the image as luxury editorial photography with sharp textile detail, realistic drape, clean edges, natural proportions, premium lighting, and a polished photographic finish.',
      'Fabric reference mapping:',
      '{{reference_rules}}',
      'Visual cues:',
      '{{fabric_cues}}',
    ].join('\n'),
    home_hero: [
      'Create one premium homepage hero banner image for {{collection_context}}.',
      'Output format is {{orientation}} sized for {{target_dimensions}}.',
      'Create one single continuous high-end fashion campaign scene. Do not create a collage, split frame, or multiple separate panels.',
      'Use only the provided fabric swatches as the textile source. Every visible patterned textile must come only from those references.',
      'Use all provided fabric swatches in the final scene. Some fabric swatches may be represented by more than one reference image. If multiple images belong to the same fabric design, treat them as one single fabric identity and use that fabric only once in the final composition.',
      'Preserve the exact print identity of each selected fabric. Keep motif character, motif scale, repeat density, and color direction consistent with the references. Do not invent a new pattern and do not merge multiple reference prints into one textile.',
      'Each selected fabric may appear only once in the scene. Do not repeat one fabric across apparel, drapes, props, background textiles, or accessories. No duplicate usage of any selected fabric anywhere in the image.',
      'The composition must feel like a premium ecommerce hero banner with elegant spacing and clean visual hierarchy. If models are present, show at least one full clearly visible face and full head in frame. Do not crop the forehead, chin, or top of the head. Avoid tight torso-only crops.',
      'The image must look premium, sharp, polished, and photographic, with realistic drape, crisp fabric detail, clean skin, clean hands, and refined luxury campaign lighting.',
      'Fabric reference mapping:',
      '{{reference_rules}}',
      'Visual cues:',
      '{{fabric_cues}}',
      'Negative constraints:',
      'No repeated fabric usage. No background echo of a used fabric. No unrelated garments or textile designs. No split screen. No multi-panel layout. No cropped faces.',
    ].join('\n'),
    collection_banner: [
      'Create one premium collection banner image for {{collection_context}}.',
      'Output format is {{orientation}} sized for {{target_dimensions}}.',
      'Create one single unified editorial scene that represents the collection elegantly. Do not create a collage, split frame, diptych, or separate images inside one frame.',
      'Use only the provided fabric swatches as the textile source. Every visible patterned textile must come only from those references.',
      'Use all provided fabric swatches in the final scene. Some fabric swatches may be represented by more than one reference image. If multiple images belong to the same fabric design, treat them as one single fabric identity and use that fabric only once in the final composition.',
      'Preserve the print identity of each selected fabric. Keep motif style, motif scale, repeat density, and color family faithful to the references. Do not invent new prints and do not merge prints together.',
      'Each selected fabric may appear only once in the composition. Keep every fabric separate and distinct. Do not repeat one fabric in apparel, drape, props, or background textiles.',
      'The image should feel refined, editorial, and commercially usable for a collection page banner. It may use models, styled fabric drape, or a balanced mix of both, but the final image must remain one cohesive photographic composition.',
      'Prioritize crisp textile detail, realistic drape, premium lighting, elegant styling, clean edges, and a polished photographic finish.',
      'Fabric reference mapping:',
      '{{reference_rules}}',
      'Visual cues:',
      '{{fabric_cues}}',
      'Negative constraints:',
      'No duplicate fabric usage. No unrelated product or textile pattern. No split layout. No fake collage effect. No muddy or low-detail fabric rendering.',
    ].join('\n'),
  },
  promptVariables: {
    use_case: '',
    orientation: '',
    reference_count: '',
    target_dimensions: '',
    collection_context: '',
    reference_rules: '',
    fabric_cues: '',
    quality_cues: '',
  },
};

function normalizeDimensionValue(value, fallback) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(400, Math.min(6000, parsed));
}

function normalizeQualityValue(value, fallback) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(40, Math.min(100, parsed));
}

function normalizeSettings(source = {}) {
  const placements = source?.placements || {};
  const output = source?.output || {};
  return {
    placements: {
      home_hero: {
        desktop: {
          width: normalizeDimensionValue(placements?.home_hero?.desktop?.width, FALLBACK_SETTINGS.placements.home_hero.desktop.width),
          height: normalizeDimensionValue(placements?.home_hero?.desktop?.height, FALLBACK_SETTINGS.placements.home_hero.desktop.height),
        },
        mobile: {
          width: normalizeDimensionValue(placements?.home_hero?.mobile?.width, FALLBACK_SETTINGS.placements.home_hero.mobile.width),
          height: normalizeDimensionValue(placements?.home_hero?.mobile?.height, FALLBACK_SETTINGS.placements.home_hero.mobile.height),
        },
      },
      collection_banner: {
        desktop: {
          width: normalizeDimensionValue(placements?.collection_banner?.desktop?.width, FALLBACK_SETTINGS.placements.collection_banner.desktop.width),
          height: normalizeDimensionValue(placements?.collection_banner?.desktop?.height, FALLBACK_SETTINGS.placements.collection_banner.desktop.height),
        },
        mobile: {
          width: normalizeDimensionValue(placements?.collection_banner?.mobile?.width, FALLBACK_SETTINGS.placements.collection_banner.mobile.width),
          height: normalizeDimensionValue(placements?.collection_banner?.mobile?.height, FALLBACK_SETTINGS.placements.collection_banner.mobile.height),
        },
      },
    },
    output: {
      format: ['jpg', 'jpeg', 'png', 'webp'].includes(String(output?.format || '').trim().toLowerCase())
        ? String(output.format).trim().toLowerCase()
        : FALLBACK_SETTINGS.output.format,
      jpegQuality: normalizeQualityValue(output?.jpegQuality, FALLBACK_SETTINGS.output.jpegQuality),
      qualityMode: ['fast', 'high'].includes(String(output?.qualityMode || '').trim().toLowerCase())
        ? String(output.qualityMode).trim().toLowerCase()
        : FALLBACK_SETTINGS.output.qualityMode,
    },
    promptTemplates: {
      universal_base: String(
        source?.promptTemplates?.universal_base ||
        source?.promptTemplate ||
        FALLBACK_SETTINGS.promptTemplates.universal_base
      ).trim() || FALLBACK_SETTINGS.promptTemplates.universal_base,
      home_hero: String(
        source?.promptTemplates?.home_hero ||
        FALLBACK_SETTINGS.promptTemplates.home_hero
      ).trim() || FALLBACK_SETTINGS.promptTemplates.home_hero,
      collection_banner: String(
        source?.promptTemplates?.collection_banner ||
        FALLBACK_SETTINGS.promptTemplates.collection_banner
      ).trim() || FALLBACK_SETTINGS.promptTemplates.collection_banner,
    },
    promptVariables: {
      use_case: String(source?.promptVariables?.use_case || '').trim(),
      orientation: String(source?.promptVariables?.orientation || '').trim(),
      reference_count: String(source?.promptVariables?.reference_count || '').trim(),
      target_dimensions: String(source?.promptVariables?.target_dimensions || '').trim(),
      collection_context: String(source?.promptVariables?.collection_context || '').trim(),
      reference_rules: String(source?.promptVariables?.reference_rules || '').trim(),
      fabric_cues: String(source?.promptVariables?.fabric_cues || '').trim(),
      quality_cues: String(source?.promptVariables?.quality_cues || '').trim(),
    },
  };
}

function formatPresetLabel(size = {}) {
  const width = Number(size?.width || 0) || 0;
  const height = Number(size?.height || 0) || 0;
  return `${width} x ${height}`;
}

function buildSizeRows(settings = {}) {
  return [
    {
      key: 'home_hero_desktop',
      label: 'Home Hero Desktop',
      placementKey: 'home_hero',
      deviceKey: 'desktop',
      width: settings?.placements?.home_hero?.desktop?.width ?? '',
      height: settings?.placements?.home_hero?.desktop?.height ?? '',
    },
    {
      key: 'home_hero_mobile',
      label: 'Home Hero Mobile',
      placementKey: 'home_hero',
      deviceKey: 'mobile',
      width: settings?.placements?.home_hero?.mobile?.width ?? '',
      height: settings?.placements?.home_hero?.mobile?.height ?? '',
    },
    {
      key: 'collection_banner_desktop',
      label: 'Collection Banner Desktop',
      placementKey: 'collection_banner',
      deviceKey: 'desktop',
      width: settings?.placements?.collection_banner?.desktop?.width ?? '',
      height: settings?.placements?.collection_banner?.desktop?.height ?? '',
    },
    {
      key: 'collection_banner_mobile',
      label: 'Collection Banner Mobile',
      placementKey: 'collection_banner',
      deviceKey: 'mobile',
      width: settings?.placements?.collection_banner?.mobile?.width ?? '',
      height: settings?.placements?.collection_banner?.mobile?.height ?? '',
    },
  ];
}

function resolveSizeSpecForKey(settings = {}, sizeKey = 'home_hero_desktop') {
  const normalized = String(sizeKey || '').trim().toLowerCase();
  if (normalized === 'home_hero_mobile') {
    return {
      sizeKey: normalized,
      width: settings?.placements?.home_hero?.mobile?.width || 1200,
      height: settings?.placements?.home_hero?.mobile?.height || 1600,
    };
  }
  if (normalized === 'collection_banner_desktop') {
    return {
      sizeKey: normalized,
      width: settings?.placements?.collection_banner?.desktop?.width || 2400,
      height: settings?.placements?.collection_banner?.desktop?.height || 900,
    };
  }
  if (normalized === 'collection_banner_mobile') {
    return {
      sizeKey: normalized,
      width: settings?.placements?.collection_banner?.mobile?.width || 1200,
      height: settings?.placements?.collection_banner?.mobile?.height || 1500,
    };
  }
  return {
    sizeKey: 'home_hero_desktop',
    width: settings?.placements?.home_hero?.desktop?.width || 2400,
    height: settings?.placements?.home_hero?.desktop?.height || 1350,
  };
}

function describeFabricCharacterForReference(reference = {}) {
  const tokens = [];
  const fabric = String(reference?.fabric || '').trim().toLowerCase();
  const quality = String(reference?.quality || '').trim().toLowerCase();
  const haystack = `${fabric} ${quality}`.trim();

  if (haystack.includes('georgette')) {
    tokens.push('lightweight', 'fluid drape', 'soft movement', 'semi-sheer feel');
  } else if (haystack.includes('organza')) {
    tokens.push('sheer', 'airy', 'crisp', 'structured volume', 'translucent finish');
  } else if (haystack.includes('chiffon')) {
    tokens.push('very lightweight', 'airy drape', 'soft flow', 'sheer feel');
  } else if (haystack.includes('satin')) {
    tokens.push('smooth surface', 'soft sheen', 'fluid fall', 'luxurious drape');
  } else if (haystack.includes('silk')) {
    tokens.push('soft sheen', 'luxurious feel', 'fluid drape', 'refined surface');
  } else if (haystack.includes('muslin')) {
    tokens.push('soft handfeel', 'breathable', 'light drape', 'matte finish');
  } else if (haystack.includes('cotton')) {
    tokens.push('soft', 'breathable', 'comfortable body', 'natural matte finish');
  } else if (haystack.includes('linen')) {
    tokens.push('airy', 'natural texture', 'relaxed drape', 'breathable feel');
  } else if (haystack.includes('viscose') || haystack.includes('rayon')) {
    tokens.push('soft handfeel', 'fluid drape', 'smooth fall', 'gentle movement');
  } else if (haystack.includes('polyester')) {
    tokens.push('smooth surface', 'clean fall', 'stable structure');
  }

  if (quality.includes('sheer') && !tokens.includes('sheer')) tokens.push('sheer');
  if (quality.includes('flow') && !tokens.includes('fluid drape')) tokens.push('fluid drape');
  if (quality.includes('soft') && !tokens.includes('soft handfeel')) tokens.push('soft handfeel');

  if (!tokens.length) {
    tokens.push('realistic drape', 'authentic surface feel');
  }

  return tokens.join(', ');
}

function buildCollectionCreativeReferenceRules(references = []) {
  const items = Array.isArray(references) ? references : [];
  if (!items.length) return 'Reference-specific mapping will be provided when fabric references are selected.';

  return items.map((reference, index) => {
    const referenceNumber = index + 1;
    const imageCount = Array.isArray(reference?.aiReferenceImageUrls)
      ? reference.aiReferenceImageUrls.filter(Boolean).length
      : 0;
    const fabricName = String(reference?.fabric || reference?.quality || reference?.title || `Fabric ${referenceNumber}`).trim();
    const fabricCharacter = describeFabricCharacterForReference(reference);
    const imageSentence = imageCount > 1
      ? 'Two reference images are provided for the same fabric swatch. Treat both images as one single fabric identity.'
      : 'One reference image is provided for this fabric swatch. Treat it as one single fabric identity.';

    return [
      `Reference ${referenceNumber}:`,
      imageSentence,
      `Fabric name: ${fabricName}`,
      `Fabric character: ${fabricCharacter}`,
      'Use this fabric only once in the final composition.',
      'Preserve its print identity, motif scale, surface character, and fabric feel.',
    ].join(' ');
  }).join('\n');
}

function buildPromptPreviewValues({ settings = {}, preview = null, selectedSizeKey = 'home_hero_desktop' } = {}) {
  const sizeSpec = resolveSizeSpecForKey(settings, selectedSizeKey);
  const width = Number(sizeSpec?.width || 0) || 0;
  const height = Number(sizeSpec?.height || 0) || 0;
  const orientation = width >= height ? 'landscape' : 'portrait';
  const isHero = String(sizeSpec?.sizeKey || '').includes('home_hero');
  const selectedReferences = Array.isArray(preview?.selectedReferences) ? preview.selectedReferences : [];
  const fabricCues = Array.from(new Set(selectedReferences.map((item) => item?.fabric).filter(Boolean)));
  const qualityCues = Array.from(new Set(selectedReferences.map((item) => item?.quality).filter(Boolean)));
  const computed = {
    use_case: isHero ? 'homepage collection hero' : 'collection banner',
    orientation,
    reference_rules: buildCollectionCreativeReferenceRules(selectedReferences),
    fabric_cues: fabricCues.length ? `Fabric cues across references: ${fabricCues.join(', ')}.` : 'Fabric cues will appear after selecting a collection.',
    quality_cues: qualityCues.length ? `Fabric quality cues: ${qualityCues.join(', ')}.` : 'Quality cues will appear after selecting a collection.',
    reference_count: String(selectedReferences.length || 0),
    target_dimensions: width && height ? `${width} x ${height}` : '—',
    collection_context: String(preview?.collection?.title || preview?.collection?.handle || preview?.collection?.id || 'No collection selected').trim(),
  };
  const overrides = settings?.promptVariables || {};
  return Object.fromEntries(
    Object.entries(computed).map(([key, value]) => [key, String(overrides?.[key] || '').trim() || value])
  );
}

const DYNAMIC_PROMPT_PLACEHOLDERS = [
  'use_case',
  'orientation',
  'reference_count',
  'target_dimensions',
  'collection_context',
  'reference_rules',
  'fabric_cues',
  'quality_cues',
];

export default function CollectionCreativesPage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [publishingDraftId, setPublishingDraftId] = useState('');
  const [pushingCollectionImageDraftId, setPushingCollectionImageDraftId] = useState('');
  const [rejectingDraftId, setRejectingDraftId] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [activePromptTemplateKey, setActivePromptTemplateKey] = useState('home_hero');
  const [settings, setSettings] = useState(FALLBACK_SETTINGS);
  const [draftSettings, setDraftSettings] = useState(FALLBACK_SETTINGS);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [collections, setCollections] = useState([]);
  const [selectedCollectionGid, setSelectedCollectionGid] = useState('');
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftsPage, setDraftsPage] = useState(1);
  const [draftsStatusFilter, setDraftsStatusFilter] = useState('all');
  const [draftsPagination, setDraftsPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [selectedSizeKey, setSelectedSizeKey] = useState('home_hero_desktop');
  const [selectedSceneFamily, setSelectedSceneFamily] = useState('auto');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authedFetch('/api/v1/collection-creatives/settings');
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load collection creative settings.');
      }
      const normalized = normalizeSettings(data?.settings || {});
      setSettings(normalized);
      setDraftSettings(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load collection creative settings.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadCollections = useCallback(async (query = '') => {
    setCollectionsLoading(true);
    try {
      const response = await authedFetch(`/api/v1/lookbook/collections?q=${encodeURIComponent(String(query || '').trim())}&limit=2000`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load collections.');
      }
      setCollections(Array.isArray(data?.collections) ? data.collections : []);
    } catch (err) {
      setError(err.message || 'Failed to load collections.');
      setCollections([]);
    } finally {
      setCollectionsLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  const loadPreview = useCallback(async (collectionGid, options = {}) => {
    const gid = String(collectionGid || '').trim();
    if (!gid) {
      setPreview(null);
      setSelectedProductIds([]);
      return;
    }
    setPreviewLoading(true);
    setError('');
    try {
      const requestedIds = Array.isArray(options.selectedProductIds) ? options.selectedProductIds : [];
      const query = new URLSearchParams({ collectionGid: gid });
      if (requestedIds.length) {
        query.set('selectedProductIds', requestedIds.join(','));
      }
      const response = await authedFetch(`/api/v1/collection-creatives/preview?${query.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load collection preview.');
      }
      setPreview(data);
      const nextSelectedIds = Array.isArray(data?.selectedReferences)
        ? data.selectedReferences.map((item) => String(item?.productId || '').trim()).filter(Boolean)
        : [];
      setSelectedProductIds(nextSelectedIds);
    } catch (err) {
      setError(err.message || 'Failed to load collection preview.');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [authedFetch]);

  const loadDrafts = useCallback(async (collectionGid = '', page = 1, status = 'all') => {
    const gid = String(collectionGid || '').trim();
    const safePage = Math.max(1, parseInt(page, 10) || 1);
    const normalizedStatus = ['all', 'under_review', 'published'].includes(String(status || '').trim().toLowerCase())
      ? String(status || '').trim().toLowerCase()
      : 'all';
    setDraftsLoading(true);
    try {
      const query = new URLSearchParams({
        limit: '12',
        page: String(safePage),
      });
      if (gid) query.set('collectionGid', gid);
      if (normalizedStatus !== 'all') query.set('status', normalizedStatus);
      const response = await authedFetch(`/api/v1/collection-creatives/drafts?${query.toString()}`);
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load collection drafts.');
      }
      setDrafts(Array.isArray(data?.drafts) ? data.drafts : []);
      setDraftsPagination({
        page: Number(data?.pagination?.page || safePage) || safePage,
        limit: Number(data?.pagination?.limit || 12) || 12,
        total: Number(data?.pagination?.total || 0) || 0,
        totalPages: Number(data?.pagination?.totalPages || 1) || 1,
        hasPreviousPage: data?.pagination?.hasPreviousPage === true,
        hasNextPage: data?.pagination?.hasNextPage === true,
      });
    } catch (err) {
      setError(err.message || 'Failed to load collection drafts.');
      setDrafts([]);
      setDraftsPagination({
        page: 1,
        limit: 12,
        total: 0,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      });
    } finally {
      setDraftsLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadDrafts(selectedCollectionGid, draftsPage, draftsStatusFilter);
  }, [draftsPage, draftsStatusFilter, loadDrafts, selectedCollectionGid]);

  const updateDraftSize = useCallback((placementKey, deviceKey, field, value) => {
    setDraftSettings((prev) => ({
      ...prev,
      placements: {
        ...prev.placements,
        [placementKey]: {
          ...prev.placements[placementKey],
          [deviceKey]: {
            ...prev.placements[placementKey][deviceKey],
            [field]: value,
          },
        },
      },
    }));
  }, []);

  const handleOpenSettings = useCallback(() => {
    setDraftSettings(settings);
    setSettingsOpen(true);
    setError('');
    setMessage('');
  }, [settings]);

  const handleOpenPrompt = useCallback(() => {
    setDraftSettings(settings);
    setActivePromptTemplateKey(String(selectedSizeKey || '').includes('collection_banner') ? 'collection_banner' : 'home_hero');
    setPromptOpen(true);
    setError('');
    setMessage('');
  }, [selectedSizeKey, settings]);

  const handleSaveSettings = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const normalized = normalizeSettings(draftSettings);
      const response = await authedFetch('/api/v1/collection-creatives/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalized),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to save collection creative settings.');
      }
      const saved = normalizeSettings(data?.settings || normalized);
      setSettings(saved);
      setDraftSettings(saved);
      setMessage(data?.message || 'Collection creative settings saved.');
      setSettingsOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save collection creative settings.');
    } finally {
      setSaving(false);
    }
  }, [authedFetch, draftSettings]);

  const summaryCards = useMemo(() => ([
    {
      title: 'Home Hero Desktop',
      value: formatPresetLabel(settings?.placements?.home_hero?.desktop),
      detail: 'Homepage wide creative',
    },
    {
      title: 'Home Hero Mobile',
      value: formatPresetLabel(settings?.placements?.home_hero?.mobile),
      detail: 'Homepage vertical crop',
    },
    {
      title: 'Collection Banner Desktop',
      value: formatPresetLabel(settings?.placements?.collection_banner?.desktop),
      detail: 'Collection page desktop banner',
    },
    {
      title: 'Collection Banner Mobile',
      value: formatPresetLabel(settings?.placements?.collection_banner?.mobile),
      detail: 'Collection page mobile banner',
    },
  ]), [settings]);

  const sizeRows = useMemo(() => buildSizeRows(draftSettings), [draftSettings]);
  const selectedCollection = useMemo(
    () => collections.find((collection) => String(collection.id || '') === String(selectedCollectionGid || '')) || null,
    [collections, selectedCollectionGid]
  );
  const effectiveSelectedReferences = useMemo(() => {
    const candidates = Array.isArray(preview?.candidates) ? preview.candidates : [];
    const byId = new Map(candidates.map((item) => [String(item?.productId || '').trim(), item]));
    const ordered = selectedProductIds
      .map((productId) => byId.get(String(productId || '').trim()))
      .filter(Boolean)
      .map((item, index) => ({ ...item, referenceOrder: index + 1 }));
    return ordered.length ? ordered : (Array.isArray(preview?.selectedReferences) ? preview.selectedReferences : []);
  }, [preview, selectedProductIds]);
  const selectedSizeLabel = useMemo(() => {
    const labels = {
      home_hero_desktop: 'Home Hero Desktop',
      home_hero_mobile: 'Home Hero Mobile',
      collection_banner_desktop: 'Collection Banner Desktop',
      collection_banner_mobile: 'Collection Banner Mobile',
    };
    return labels[selectedSizeKey] || 'Home Hero Desktop';
  }, [selectedSizeKey]);
  const promptPreviewValues = useMemo(
    () => buildPromptPreviewValues({
      settings: draftSettings,
      preview: preview ? { ...preview, selectedReferences: effectiveSelectedReferences } : preview,
      selectedSizeKey,
    }),
    [draftSettings, effectiveSelectedReferences, preview, selectedSizeKey]
  );
  const minimumSelectableCount = useMemo(() => {
    const eligible = Array.isArray(preview?.candidates) ? preview.candidates.length : 0;
    return eligible >= 3 ? 3 : Math.min(eligible, 2);
  }, [preview]);

  const toggleSelectedProduct = useCallback((productId) => {
    const normalizedId = String(productId || '').trim();
    if (!normalizedId) return;
    setSelectedProductIds((prev) => {
      const exists = prev.includes(normalizedId);
      if (exists) {
        if (prev.length <= minimumSelectableCount) {
          setError(`Keep at least ${minimumSelectableCount} selected products.`);
          return prev;
        }
        return prev.filter((item) => item !== normalizedId);
      }
      if (prev.length >= 4) {
        setError('You can select up to 4 products.');
        return prev;
      }
      setError('');
      return [...prev, normalizedId];
    });
  }, [minimumSelectableCount]);

  const handleGenerateDraft = useCallback(async () => {
    if (!selectedCollectionGid) {
      setError('Select a collection first.');
      return;
    }
    if (selectedProductIds.length < minimumSelectableCount) {
      setError(`Select at least ${minimumSelectableCount} products first.`);
      return;
    }
    setGeneratingDraft(true);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch('/api/v1/collection-creatives/generate-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collectionGid: selectedCollectionGid,
          sizeKey: selectedSizeKey,
          sceneFamilyOverride: selectedSceneFamily,
          selectedProductIds,
        }),
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to generate collection draft.');
      }
      setMessage(data?.message || `Draft generated for ${selectedSizeLabel}.`);
      await loadDrafts(selectedCollectionGid, draftsPage, draftsStatusFilter);
    } catch (err) {
      setError(err.message || 'Failed to generate collection draft.');
    } finally {
      setGeneratingDraft(false);
    }
  }, [authedFetch, draftsPage, draftsStatusFilter, loadDrafts, minimumSelectableCount, selectedCollectionGid, selectedProductIds, selectedSceneFamily, selectedSizeKey, selectedSizeLabel]);

  const handlePublishDraft = useCallback(async (draftId) => {
    const normalizedDraftId = String(draftId || '').trim();
    if (!normalizedDraftId) return;
    setPublishingDraftId(normalizedDraftId);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch(`/api/v1/collection-creatives/drafts/${encodeURIComponent(normalizedDraftId)}/publish`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to publish draft.');
      }
      setMessage(data?.message || 'Draft published to Shopify.');
      await loadDrafts(selectedCollectionGid, draftsPage, draftsStatusFilter);
    } catch (err) {
      setError(err.message || 'Failed to publish draft.');
    } finally {
      setPublishingDraftId('');
    }
  }, [authedFetch, draftsPage, draftsStatusFilter, loadDrafts, selectedCollectionGid]);

  const handlePushCollectionImage = useCallback(async (draftId) => {
    const normalizedDraftId = String(draftId || '').trim();
    if (!normalizedDraftId) return;
    setPushingCollectionImageDraftId(normalizedDraftId);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch(`/api/v1/collection-creatives/drafts/${encodeURIComponent(normalizedDraftId)}/push-collection-image`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to push collection image.');
      }
      setMessage(data?.message || 'Draft image pushed to Shopify collection image.');
      await loadDrafts(selectedCollectionGid, draftsPage, draftsStatusFilter);
    } catch (err) {
      setError(err.message || 'Failed to push collection image.');
    } finally {
      setPushingCollectionImageDraftId('');
    }
  }, [authedFetch, draftsPage, draftsStatusFilter, loadDrafts, selectedCollectionGid]);

  const handleRejectRegenerateDraft = useCallback(async (draftId) => {
    const normalizedDraftId = String(draftId || '').trim();
    if (!normalizedDraftId) return;
    setRejectingDraftId(normalizedDraftId);
    setError('');
    setMessage('');
    try {
      const response = await authedFetch(`/api/v1/collection-creatives/drafts/${encodeURIComponent(normalizedDraftId)}/reject-regenerate`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to reject and regenerate draft.');
      }
      setMessage(data?.message || 'Draft rejected and regenerated.');
      await loadDrafts(selectedCollectionGid, draftsPage, draftsStatusFilter);
      if (selectedCollectionGid) {
        await loadPreview(selectedCollectionGid);
      }
    } catch (err) {
      setError(err.message || 'Failed to reject and regenerate draft.');
    } finally {
      setRejectingDraftId('');
    }
  }, [authedFetch, draftsPage, draftsStatusFilter, loadDrafts, loadPreview, selectedCollectionGid]);

  return (
    <div className="collection-creatives-admin">
      <div className="collection-creatives-admin__header">
        <div>
          <div className="collection-creatives-admin__title-row">
            <h1>Collection Creatives</h1>
            <button
              type="button"
              className="collection-creatives-admin__settings-icon"
              aria-label="Collection creative prompt"
              title="Collection creative prompt"
              onClick={handleOpenPrompt}
            >
              ✎
            </button>
            <button
              type="button"
              className="collection-creatives-admin__settings-icon"
              aria-label="Collection creative settings"
              title="Collection creative settings"
              onClick={handleOpenSettings}
            >
              ⚙
            </button>
          </div>
          <p>Manual setup page for collection-level creative generation. Image sizes are editable from settings so generation logic does not need hardcoded presets.</p>
        </div>
        <div className="collection-creatives-admin__header-actions">
          <button type="button" className="collection-creatives-admin__refresh" onClick={loadSettings} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? <div className="collection-creatives-admin__banner is-error">{error}</div> : null}
      {message ? <div className="collection-creatives-admin__banner is-success">{message}</div> : null}

      <section className="collection-creatives-admin__summary">
        {summaryCards.map((card) => (
          <article key={card.title} className="collection-creatives-admin__summary-card">
            <span>{card.title}</span>
            <strong>{card.value}</strong>
            <small>{card.detail}</small>
          </article>
        ))}
      </section>

      <section className="collection-creatives-admin__panel">
        <h2>Blend Mode Collection Picker</h2>
        <div className="collection-creatives-admin__picker">
          <label className="is-wide">
            Search collection
            <input
              value={collectionSearch}
              onChange={(e) => {
                const next = e.target.value;
                setCollectionSearch(next);
                loadCollections(next);
              }}
              placeholder="Search collection title or handle"
            />
          </label>
          {!!String(collectionSearch || '').trim() && !!collections.length && (
            <div className="collection-creatives-admin__picker-results">
              {collections.slice(0, 50).map((collection) => (
                <button
                  type="button"
                  key={collection.id}
                  className={`collection-creatives-admin__picker-result ${selectedCollection?.id === collection.id ? 'is-selected' : ''}`}
                  onClick={async () => {
                    setDraftsPage(1);
                    setSelectedCollectionGid(collection.id);
                    await loadPreview(collection.id);
                  }}
                >
                  <span>{collection.title || collection.handle || collection.id}</span>
                  <small>{collection.products_count ?? '—'} products</small>
                </button>
              ))}
            </div>
          )}
          {collectionsLoading && (
            <div className="collection-creatives-admin__picker-note">Loading collections...</div>
          )}
          {selectedCollection && (
            <div className="collection-creatives-admin__selected">
              <strong>Selected:</strong> {selectedCollection.title || selectedCollection.handle || selectedCollection.id}
              <button
                type="button"
                className="collection-creatives-admin__refresh"
                onClick={async () => {
                  await Promise.all([
                    loadPreview(selectedCollection.id),
                    loadDrafts(selectedCollection.id, draftsPage, draftsStatusFilter),
                  ]);
                }}
                disabled={previewLoading}
              >
                {previewLoading ? 'Refreshing references...' : 'Refresh References'}
              </button>
              <button
                type="button"
                className="collection-creatives-admin__refresh"
                onClick={() => {
                  setSelectedCollectionGid('');
                  setCollectionSearch('');
                  setPreview(null);
                  setSelectedProductIds([]);
                  setDraftsPage(1);
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="collection-creatives-admin__panel">
        <h2>Blend Mode Reference Preview</h2>
        {previewLoading && (
          <div className="collection-creatives-admin__empty">Loading collection references...</div>
        )}
        {!previewLoading && !preview && (
          <div className="collection-creatives-admin__empty">
            Select a collection to auto-pick 3 to 4 reference fabrics. The AI will use the first and last image of each selected product for motif-scale and print context.
          </div>
        )}
        {!previewLoading && preview && (
          <>
            <div className="collection-creatives-admin__preview-meta">
              <div><span>Collection</span><strong>{preview?.collection?.title || preview?.collection?.handle || preview?.collection?.id || '—'}</strong></div>
              <div><span>Mode</span><strong>{String(preview?.selectionMode || 'blend').replace(/_/g, ' ')}</strong></div>
              <div><span>Inspected</span><strong>{preview?.inspectedCount || 0}</strong></div>
              <div><span>Selected</span><strong>{effectiveSelectedReferences.length || 0}</strong></div>
            </div>

            <div className="collection-creatives-admin__reference-grid">
              {effectiveSelectedReferences.map((item) => (
                <article key={`selected-${item.productId}`} className="collection-creatives-admin__reference-card is-selected">
                  <div className="collection-creatives-admin__reference-badge">
                    {`Reference ${item.referenceOrder}`}
                  </div>
                  {item.imageUrl ? <img src={item.imageUrl} alt={item.title || `Reference ${item.referenceOrder}`} /> : null}
                  <div className="collection-creatives-admin__reference-body">
                    <strong>{item.title || item.productId}</strong>
                    <small>{item.fabric || 'Fabric unknown'}{item.quality ? ` • ${item.quality}` : ''}</small>
                    <small>{item.detailImageUrl ? 'Uses first and last gallery image as AI references' : 'Uses primary product image as AI reference'}</small>
                    <small>{item.productType || 'Product type unknown'}</small>
                  </div>
                </article>
              ))}
            </div>

            <div className="collection-creatives-admin__candidate-head">
              <h3>Eligible Candidates</h3>
              <span>{preview?.eligibleCount || 0} with usable images</span>
            </div>
            <div className="collection-creatives-admin__candidate-grid">
              {(preview?.candidates || []).map((item) => (
                <article
                  key={`candidate-${item.productId}`}
                  className={`collection-creatives-admin__reference-card ${selectedProductIds.includes(String(item.productId || '').trim()) ? 'is-selected' : ''}`}
                >
                  {item.imageUrl ? <img src={item.imageUrl} alt={item.title || item.productId} /> : null}
                  <div className="collection-creatives-admin__reference-body">
                    <strong>{item.title || item.productId}</strong>
                    <small>{item.fabric || 'Fabric unknown'}{item.quality ? ` • ${item.quality}` : ''}</small>
                    <small>{item.productType || 'Product type unknown'}</small>
                    <button
                      type="button"
                      className="collection-creatives-admin__candidate-toggle"
                      onClick={() => toggleSelectedProduct(item.productId)}
                    >
                      {selectedProductIds.includes(String(item.productId || '').trim()) ? 'Selected' : 'Select'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="collection-creatives-admin__panel">
        <h2>Generate Draft</h2>
        <div className="collection-creatives-admin__generate-row">
          <label>
            Image Type
            <select value={selectedSizeKey} onChange={(e) => setSelectedSizeKey(e.target.value)}>
              <option value="home_hero_desktop">Home Hero Desktop</option>
              <option value="home_hero_mobile">Home Hero Mobile</option>
              <option value="collection_banner_desktop">Collection Banner Desktop</option>
              <option value="collection_banner_mobile">Collection Banner Mobile</option>
            </select>
          </label>
          <label>
            Scene Mode
            <select value={selectedSceneFamily} onChange={(e) => setSelectedSceneFamily(e.target.value)}>
              {SCENE_FAMILY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="collection-creatives-admin__generate-btn"
            onClick={handleGenerateDraft}
            disabled={!selectedCollectionGid || previewLoading || generatingDraft}
          >
            {generatingDraft ? 'Generating Draft...' : 'Generate Draft'}
          </button>
        </div>
        <p className="collection-creatives-admin__helper">
          The system defaults to 3 to 4 collection products, lets you change the selection manually, and uses only those selected product references while generating the creative.
        </p>
      </section>

      <section className="collection-creatives-admin__panel">
        <div className="collection-creatives-admin__drafts-head">
          <div>
            <h2>Generated Drafts</h2>
            <p className="collection-creatives-admin__helper">
              Sorted by latest generated first.
              {selectedCollectionGid
                ? ` Showing drafts for ${selectedCollection?.title || selectedCollection?.handle || selectedCollectionGid}.`
                : ' Showing all generated drafts.'}
            </p>
          </div>
          <div className="collection-creatives-admin__drafts-meta">
            <label>
              Filter
              <select
                value={draftsStatusFilter}
                onChange={(e) => {
                  setDraftsStatusFilter(e.target.value);
                  setDraftsPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="under_review">Under Review</option>
                <option value="published">Published</option>
              </select>
            </label>
            <small>Total: {draftsPagination.total}</small>
            <small>Page {draftsPagination.page} of {draftsPagination.totalPages}</small>
          </div>
        </div>
        {draftsLoading && (
          <div className="collection-creatives-admin__empty">Loading drafts...</div>
        )}
        {!draftsLoading && !drafts.length && (
          <div className="collection-creatives-admin__empty">
            No generated drafts found yet.
          </div>
        )}
        {!draftsLoading && !!drafts.length && (
          <>
            <div className="collection-creatives-admin__draft-grid">
              {drafts.map((draft) => (
                <article key={draft.id} className="collection-creatives-admin__draft-card">
                  {draft.imageUrl ? (
                    <a
                      href={draft.imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="collection-creatives-admin__draft-image-link"
                      title="Open full-size image in new tab"
                    >
                      <img
                        src={draft.imageUrl}
                        alt={draft.collectionTitle || draft.id}
                        style={{
                          aspectRatio: (draft?.imageWidth && draft?.imageHeight)
                            ? `${draft.imageWidth} / ${draft.imageHeight}`
                            : (draft?.sizeSpec?.width && draft?.sizeSpec?.height)
                              ? `${draft.sizeSpec.width} / ${draft.sizeSpec.height}`
                              : undefined,
                        }}
                      />
                    </a>
                  ) : null}
                  <div className="collection-creatives-admin__draft-body">
                    <strong>{draft.collectionTitle || draft.collectionHandle || draft.collectionGid}</strong>
                    <small>{draft.sizeSpec?.width || '—'} x {draft.sizeSpec?.height || '—'} • {draft.sizeKey || '—'}</small>
                    <small>Scene: {String(draft.sceneFamily || '—').replace(/_/g, ' ')}</small>
                    <small>Status: {draft.status || 'generated'}</small>
                    {draft.publishedMetaobjectId ? (
                      <small>Metaobject: {draft.publishedMetaobjectId}</small>
                    ) : null}
                    <div className="collection-creatives-admin__draft-actions">
                      {String(draft.sizeKey || '').trim().toLowerCase() === 'collection_banner_desktop' ? (
                        <button
                          type="button"
                          className="collection-creatives-admin__reject-btn"
                          onClick={() => handlePushCollectionImage(draft.id)}
                          disabled={
                            pushingCollectionImageDraftId === draft.id ||
                            rejectingDraftId === draft.id ||
                            publishingDraftId === draft.id
                          }
                        >
                          {pushingCollectionImageDraftId === draft.id ? 'Pushing Image...' : 'Push As Collection Image'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="collection-creatives-admin__reject-btn"
                        onClick={() => handleRejectRegenerateDraft(draft.id)}
                        disabled={
                          rejectingDraftId === draft.id ||
                          publishingDraftId === draft.id ||
                          String(draft.status || '').trim().toLowerCase() === 'published'
                        }
                      >
                        {rejectingDraftId === draft.id ? 'Rejecting...' : 'Reject & Regenerate'}
                      </button>
                      <button
                        type="button"
                        className="collection-creatives-admin__publish-btn"
                        onClick={() => handlePublishDraft(draft.id)}
                        disabled={
                          publishingDraftId === draft.id ||
                          rejectingDraftId === draft.id ||
                          String(draft.status || '').trim().toLowerCase() === 'published'
                        }
                      >
                        {String(draft.status || '').trim().toLowerCase() === 'published'
                          ? 'Published'
                          : publishingDraftId === draft.id
                            ? 'Publishing...'
                            : 'Approve & Publish'}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="collection-creatives-admin__draft-pagination">
              <button
                type="button"
                className="collection-creatives-admin__refresh"
                onClick={() => setDraftsPage((prev) => Math.max(1, prev - 1))}
                disabled={!draftsPagination.hasPreviousPage || draftsLoading}
              >
                Previous
              </button>
              <span>Page {draftsPagination.page} of {draftsPagination.totalPages}</span>
              <button
                type="button"
                className="collection-creatives-admin__refresh"
                onClick={() => setDraftsPage((prev) => prev + 1)}
                disabled={!draftsPagination.hasNextPage || draftsLoading}
              >
                Next
              </button>
            </div>
          </>
        )}
      </section>

      <section className="collection-creatives-admin__panel">
        <h2>Current Generation Presets</h2>
        <div className="collection-creatives-admin__preset-grid">
          <article className="collection-creatives-admin__preset-card">
            <h3>Home Hero</h3>
            <p>Desktop: {formatPresetLabel(settings?.placements?.home_hero?.desktop)}</p>
            <p>Mobile: {formatPresetLabel(settings?.placements?.home_hero?.mobile)}</p>
          </article>
          <article className="collection-creatives-admin__preset-card">
            <h3>Collection Banner</h3>
            <p>Desktop: {formatPresetLabel(settings?.placements?.collection_banner?.desktop)}</p>
            <p>Mobile: {formatPresetLabel(settings?.placements?.collection_banner?.mobile)}</p>
          </article>
          <article className="collection-creatives-admin__preset-card">
            <h3>Output</h3>
            <p>Format: {String(settings?.output?.format || 'jpg').toUpperCase()}</p>
            <p>JPEG Quality: {settings?.output?.jpegQuality || 88}</p>
            <p>Quality Mode: {String(settings?.output?.qualityMode || 'high').toUpperCase()}</p>
          </article>
        </div>
      </section>

      <section className="collection-creatives-admin__panel">
        <h2>Next Build Scope</h2>
        <div className="collection-creatives-admin__empty">
          Manual generate, review, approval, and Shopify publish flow will be added on top of these settings. This page currently establishes the editable image specification source of truth.
        </div>
      </section>

      {settingsOpen && (
        <div className="collection-creatives-admin__modal-backdrop" role="presentation">
          <div className="collection-creatives-admin__modal" role="dialog" aria-modal="true" aria-label="Collection creative settings">
            <div className="collection-creatives-admin__modal-header">
              <h2>Collection Creative Settings</h2>
              <button type="button" className="secondary" onClick={() => setSettingsOpen(false)}>
                Close
              </button>
            </div>
            <p className="collection-creatives-admin__modal-note">
              These image sizes will be used by the upcoming manual generation workflow and can be adjusted later without redeploying code.
            </p>

            <div className="collection-creatives-admin__modal-body">
              <section className="collection-creatives-admin__settings-group">
                <h3>Image Sizes</h3>
                <div className="collection-creatives-admin__settings-table-wrap">
                  <table className="collection-creatives-admin__settings-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Width</th>
                        <th>Height</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sizeRows.map((row) => (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          <td>
                            <input
                              type="number"
                              min="400"
                              max="6000"
                              value={row.width}
                              onChange={(e) => updateDraftSize(row.placementKey, row.deviceKey, 'width', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="400"
                              max="6000"
                              value={row.height}
                              onChange={(e) => updateDraftSize(row.placementKey, row.deviceKey, 'height', e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="collection-creatives-admin__settings-group">
                <h3>Output</h3>
                <div className="collection-creatives-admin__settings-grid">
                  <label>
                    Quality Mode
                    <select
                      value={draftSettings?.output?.qualityMode || 'high'}
                      onChange={(e) => setDraftSettings((prev) => ({
                        ...prev,
                        output: { ...prev.output, qualityMode: e.target.value },
                      }))}
                    >
                      <option value="high">High</option>
                      <option value="fast">Fast</option>
                    </select>
                  </label>
                  <label>
                    Format
                    <select
                      value={draftSettings?.output?.format || 'jpg'}
                      onChange={(e) => setDraftSettings((prev) => ({
                        ...prev,
                        output: { ...prev.output, format: e.target.value },
                      }))}
                    >
                      <option value="jpg">JPG</option>
                      <option value="jpeg">JPEG</option>
                      <option value="png">PNG</option>
                      <option value="webp">WEBP</option>
                    </select>
                  </label>
                  <label>
                    JPEG Quality
                    <input
                      type="number"
                      min="40"
                      max="100"
                      value={draftSettings?.output?.jpegQuality ?? ''}
                      onChange={(e) => setDraftSettings((prev) => ({
                        ...prev,
                        output: { ...prev.output, jpegQuality: e.target.value },
                      }))}
                    />
                  </label>
                </div>
              </section>
            </div>

            <div className="collection-creatives-admin__modal-actions">
              <button type="button" className="secondary" onClick={() => {
                setDraftSettings(settings);
                setSettingsOpen(false);
              }}>
                Cancel
              </button>
              <button type="button" onClick={handleSaveSettings} disabled={saving}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}

      {promptOpen && (
        <div className="collection-creatives-admin__modal-backdrop" role="presentation">
          <div className="collection-creatives-admin__modal collection-creatives-admin__modal--prompt" role="dialog" aria-modal="true" aria-label="Collection creative prompt">
            <div className="collection-creatives-admin__modal-header">
              <h2>Collection Creative Prompt</h2>
              <button type="button" className="secondary" onClick={() => setPromptOpen(false)}>
                Close
              </button>
            </div>
            <p className="collection-creatives-admin__modal-note">
              Edit the prompt template used for each image type. Generation automatically uses the Home Hero template for hero sizes and the Collection Banner template for banner sizes.
            </p>

            <div className="collection-creatives-admin__modal-body">
              <section className="collection-creatives-admin__settings-group">
                <div className="collection-creatives-admin__prompt-template-tabs">
                  <button
                    type="button"
                    className={activePromptTemplateKey === 'home_hero' ? 'is-active' : ''}
                    onClick={() => setActivePromptTemplateKey('home_hero')}
                  >
                    Home Hero
                  </button>
                  <button
                    type="button"
                    className={activePromptTemplateKey === 'collection_banner' ? 'is-active' : ''}
                    onClick={() => setActivePromptTemplateKey('collection_banner')}
                  >
                    Collection Banner
                  </button>
                  <button
                    type="button"
                    className={activePromptTemplateKey === 'universal_base' ? 'is-active' : ''}
                    onClick={() => setActivePromptTemplateKey('universal_base')}
                  >
                    Universal Fallback
                  </button>
                </div>
                <h3>Prompt Template</h3>
                <textarea
                  className="collection-creatives-admin__prompt-textarea"
                  value={draftSettings?.promptTemplates?.[activePromptTemplateKey] || ''}
                  onChange={(e) => setDraftSettings((prev) => ({
                    ...prev,
                    promptTemplates: {
                      ...(prev.promptTemplates || {}),
                      [activePromptTemplateKey]: e.target.value,
                    },
                  }))}
                  spellCheck={false}
                />
              </section>

              <section className="collection-creatives-admin__settings-group">
                <h3>Available Placeholders</h3>
                {!selectedCollectionGid ? (
                  <p className="collection-creatives-admin__modal-note">
                    Select a collection above to preview collection-specific placeholder values.
                  </p>
                ) : null}
                <div className="collection-creatives-admin__placeholder-values">
                  {DYNAMIC_PROMPT_PLACEHOLDERS.map((key) => (
                    <div key={key} className="collection-creatives-admin__placeholder-item">
                      <code>{`{{${key}}}`}</code>
                      <label>
                        Override Value
                        <textarea
                          className="collection-creatives-admin__placeholder-textarea"
                          value={draftSettings?.promptVariables?.[key] || ''}
                          onChange={(e) => setDraftSettings((prev) => ({
                            ...prev,
                            promptVariables: {
                              ...(prev.promptVariables || {}),
                              [key]: e.target.value,
                            },
                          }))}
                          placeholder="Leave blank to use automatic value"
                          spellCheck={false}
                        />
                      </label>
                      <div className="collection-creatives-admin__placeholder-preview">
                        <span>Current Value</span>
                        <p>{String(promptPreviewValues?.[key] || '—')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div className="collection-creatives-admin__modal-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => setDraftSettings((prev) => ({
                  ...prev,
                  promptTemplates: {
                    ...(prev.promptTemplates || {}),
                    [activePromptTemplateKey]: FALLBACK_SETTINGS.promptTemplates[activePromptTemplateKey],
                  },
                  promptVariables: { ...FALLBACK_SETTINGS.promptVariables },
                }))}
              >
                Reset Current Template
              </button>
              <button type="button" className="secondary" onClick={() => {
                setDraftSettings(settings);
                setPromptOpen(false);
              }}>
                Cancel
              </button>
              <button type="button" onClick={handleSaveSettings} disabled={saving}>
                {saving ? 'Saving...' : 'Save Prompt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
